import React, { useState, useEffect } from "react";
import {
  Loader,
  Icon,
  Modal,
  Header,
  Button,
  Form,
  Radio,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Image,
} from "semantic-ui-react";
import { Card } from "antd";
import { useTranslation } from "react-i18next";

import {
  RutasPanel,
  ScenesPanel,
  SceneIframePanel,
  EvaluationStudents,
} from "./RutasEvaluationList";
import { QuizApi } from "../../../api/quiz";
import { StudentAPI } from "../../../api/studentAPI";
import { NEW_ITINERARIOS } from "../../../ui/featureFlags";
import { ItinerariosView } from "./ItinerariosNew";

import styles from "./TeacherEvaluationGrid.module.scss";
import "./TeacherEvaluationGrid.scss";

const quizApi = new QuizApi();
const studentAPI = new StudentAPI();

export function TeacherEvaluationGrid({ teacher, accessToken }) {
  const { t } = useTranslation("evaluations");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedClassNumber, setSelectedClassNumber] = useState(null);

  const [iframeShortLink, setIframeShortLink] = useState(null);
  const [iframeFallbackUrl, setIframeFallbackUrl] = useState(null);
  const [sceneData, setSceneData] = useState(null);

  const [quizzesList, setQuizzesList] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedSceneForQuiz, setSelectedSceneForQuiz] = useState(null);
  const [selectedQuizId, setSelectedQuizId] = useState(null);

  const [activeQuizId, setActiveQuizId] = useState(null);
  const [rankingQuizName, setRankingQuizName] = useState("");
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [rankingData, setRankingData] = useState([]);

  useEffect(() => {
    if (!teacher?._id) return;

    let active = true;

    (async () => {
      setLoadingQuizzes(true);
      try {
        const { quizzes } = await quizApi.getAllQuizzes(
          accessToken,
          teacher._id,
        );
        if (!active) return;
        setQuizzesList(quizzes || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error cargando quizzes:", err);
      } finally {
        if (active) setLoadingQuizzes(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [teacher, accessToken]);

  const openScene = (scene) => {
    setSceneData(scene);
    if (scene.shortLink && scene.shortLink !== "No shortLink") {
      setIframeShortLink(scene.shortLink);
      setIframeFallbackUrl(null);
    } else {
      setIframeShortLink(null);
      setIframeFallbackUrl(scene.urlsala);
    }
  };

  const handleEnterScene = (scene) => {
    if (!quizzesList.length) {
      openScene(scene);
      setActiveQuizId(null);
      setRankingQuizName("");
    } else {
      setSelectedSceneForQuiz(scene);
      setSelectedQuizId(null);
      setShowQuizModal(true);
    }
  };

  const handleConfirmQuiz = async () => {
    try {
      await quizApi.setActiveTeacherQuiz(
        accessToken,
        teacher._id,
        selectedQuizId,
      );
      setActiveQuizId(selectedQuizId);
      const quizObj = quizzesList.find((q) => q._id === selectedQuizId);
      setRankingQuizName(quizObj?.name || "");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error asignando quiz activo:", err);
    }
    setShowQuizModal(false);
    openScene(selectedSceneForQuiz);
  };

  const handleSkipQuiz = async () => {
    try {
      await quizApi.setActiveTeacherQuiz(accessToken, teacher._id, null);
      setActiveQuizId(null);
      setRankingQuizName("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error desasignando quiz activo:", err);
    }
    setShowQuizModal(false);
    openScene(selectedSceneForQuiz);
  };

  const handleRankingClick = async (quizId) => {
    setShowRankingModal(true);
    setLoadingRanking(true);

    try {
      const quizObj = quizzesList.find((q) => q._id === quizId);
      setRankingQuizName(quizObj?.name || "");

      const result = await quizApi.getRankingResults(
        accessToken,
        teacher._id,
        quizId,
        selectedClassNumber,
      );
      const ranking = result.ranking || [];

      const studentsRes = await studentAPI.getStudentsByClassnumber(
        accessToken,
        teacher.institution._id,
        selectedClassNumber,
      );
      const studentsList = studentsRes.students || [];

      const enriched = ranking.map((item) => {
        const student = studentsList.find((s) => s._id === item.studentId);
        return { ...item, avatar: student?.avatar };
      });

      setRankingData(enriched);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error obteniendo ranking o estudiantes:", err);
      setRankingData([]);
    } finally {
      setLoadingRanking(false);
    }
  };

  // ===== REDISENO F4 (flag NEW_ITINERARIOS) =====
  if (NEW_ITINERARIOS) {
    return <ItinerariosView teacher={teacher} accessToken={accessToken} />;
  }

  // ===== LEGACY (Semantic UI) =====
  return (
    <div className={`${styles.wrap} teacher-evaluation-grid`}>
      <div className={styles.columns}>
        {/* IZQUIERDA (FULL WIDTH DISPONIBLE) */}
        <Card
          className={styles.leftCard}
          variant="outlined"
          styles={{ body: { padding: 16 } }}
        >
          <div className="teacher-evaluation-grid__left">
            <RutasPanel
              teacher={teacher._id}
              accessToken={accessToken}
              hideHeader={true}
              onSelectRoute={({ routeId, classnumber }) => {
                setSelectedRouteId(routeId);
                setSelectedClassNumber(classnumber);
                setIframeShortLink(null);
                setIframeFallbackUrl(null);
                setSceneData(null);
                setActiveQuizId(null);
                setRankingQuizName("");
              }}
            />

            <ScenesPanel
              routeId={selectedRouteId}
              accessToken={accessToken}
              onEnterScene={handleEnterScene}
            />

            <SceneIframePanel
              shortLink={iframeShortLink}
              fallbackUrl={iframeFallbackUrl}
              accessToken={accessToken}
              routeId={selectedRouteId}
              scene={sceneData}
            />
          </div>
        </Card>

        {/* DERECHA */}
        <Card
          className={styles.rightCard}
          variant="outlined"
          styles={{ body: { padding: 16 } }}
        >
          <div className="teacher-evaluation-grid__right">
            <EvaluationStudents
              teacherInstitutionId={teacher.institution._id}
              classNumber={selectedClassNumber}
              accessToken={accessToken}
              route={selectedRouteId}
              activeQuizId={activeQuizId}
              onRankingClick={handleRankingClick}
            />
          </div>
        </Card>
      </div>

      {/* MODAL SELECCIÓN QUIZ */}
      <Modal
        className="quiz-modal"
        dimmer="blurring"
        open={showQuizModal}
        size="small"
      >
        <Header icon="tasks" content={t("evaluationGrid.selectQuizContent")} />
        <Modal.Content>
          {loadingQuizzes ? (
            <Loader active inline="centered" />
          ) : (
            <Form>
              {quizzesList.map((q) => (
                <Form.Field key={q._id}>
                  <Radio
                    label={q.name}
                    name="quizRadio"
                    value={q._id}
                    checked={selectedQuizId === q._id}
                    onChange={(_, { value }) => setSelectedQuizId(value)}
                  />
                </Form.Field>
              ))}
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="grey" inverted onClick={handleSkipQuiz}>
            <Icon name="times" /> {t("evaluationGrid.enterWithoutTest")}
          </Button>
          <Button
            color="grey"
            inverted
            onClick={handleConfirmQuiz}
            disabled={!selectedQuizId}
          >
            <Icon name="check" /> {t("evaluationGrid.assignAndEnter")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* MODAL RANKING */}
      <Modal
        className="ranking-modal"
        dimmer="blurring"
        open={showRankingModal}
        onClose={() => setShowRankingModal(false)}
        size="small"
      >
        <Header
          icon="chart bar"
          content={`${t("evaluationGrid.rankingForQuiz")}: ${rankingQuizName}`}
        />
        <Modal.Content>
          {loadingRanking ? (
            <Loader active inline="centered">
              {t("evaluationGrid.rankingLoading")}
            </Loader>
          ) : (
            <Table basic="very" celled>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>#</TableHeaderCell>
                  <TableHeaderCell>
                    {t("evaluationGrid.colStudent")}
                  </TableHeaderCell>
                  <TableHeaderCell>
                    {t("evaluationGrid.colCorrect")}
                  </TableHeaderCell>
                  <TableHeaderCell>
                    {t("evaluationGrid.colIncorrect")}
                  </TableHeaderCell>
                  <TableHeaderCell>
                    {t("evaluationGrid.colTime")}
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankingData.map((item, idx) => (
                  <TableRow key={item.studentId}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Header as="h4" image>
                        {item.avatar ? (
                          <Image src={item.avatar} rounded size="mini" />
                        ) : (
                          <Icon name="user circle" size="mini" />
                        )}
                        <Header.Content>
                          {item.studentName}
                          <Header.Subheader>
                            {selectedClassNumber}
                          </Header.Subheader>
                        </Header.Content>
                      </Header>
                    </TableCell>
                    <TableCell>{item.totalCorrect}</TableCell>
                    <TableCell>{item.totalIncorrect}</TableCell>
                    <TableCell>{item.timeTaken}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            color="blue"
            inverted
            onClick={() => setShowRankingModal(false)}
          >
            <Icon name="close" /> {t("evaluationGrid.closeButton")}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
